import { useDreamComponentsContext } from '@/dreamComponentProvider';
import { type FC, Fragment, type ReactNode } from 'react';

type PictureElementProps = {
  src?: string;
  breakpoint?: string | number;
  children: ReactNode;
} & React.ComponentProps<'picture'>;

// children should be the base image
export const PictureElement: FC<PictureElementProps> = ({ src, breakpoint, children, ...rest }) => {
  const { responsiveImagesEnabled, responsiveImagesBreakpoints } = useDreamComponentsContext();

  if (!responsiveImagesEnabled || !responsiveImagesBreakpoints || !src) return children;

  let breakPoints = responsiveImagesBreakpoints;
  if (typeof breakpoint === 'string' && breakpoint?.endsWith('px')) {
    const width = Number(breakpoint.replace('px', ''));
    if (!Number.isNaN(width)) {
      breakPoints = [...responsiveImagesBreakpoints, width];
      breakPoints.sort();
    }
  } else if (typeof breakpoint === 'number') {
    const width = breakpoint;
    if (!Number.isNaN(width)) {
      breakPoints = [...responsiveImagesBreakpoints, width];
      breakPoints.sort();
    }
  }

  return (
    <picture {...rest}>
      {breakPoints.map((width) => (
        <Fragment key={`fragment-${width}`}>
          {/* handle retina displays by providing 2x density images */}
          <source media={`(max-width: ${width}px) and (min-resolution: 2dppx)`} srcSet={getImageUrl(src, width, 2)} />
          <source media={`(max-width: ${width}px)`} srcSet={getImageUrl(src, width)} />
        </Fragment>
      ))}
      {children}
    </picture>
  );
};

function getImageUrl(src: string | undefined, width: number, density = 1) {
  return `${src?.replace('format=auto', `format=auto,width=${width * density}`)} ${density}x`;
}
